import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MonAssistant } from "@/components/assistant/MonAssistant";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatEuros, formatNombre } from "@/lib/format";
import { FicheCarte } from "@/components/FicheCarte";
import { Vide } from "@/components/commun";
import { useMesVues, useStats, VuesOnglets, ProcessusPills } from "@/components/vues/commun";
import { CarteFleches } from "@/components/vues/CarteFleches";
import { Entonnoir } from "@/components/vues/Entonnoir";
import { Couloirs } from "@/components/vues/Couloirs";
import { PosteAgent } from "@/components/poste/PosteAgent";
import { PosteConfirmateur } from "@/components/confirmation/PosteConfirmateur";
import { PostePartenaire } from "@/components/partenaire/PostePartenaire";
import { DevisSansRelance } from "@/pages/Devis";
import { AccueilDirigeant } from "@/components/tableaux/AccueilDirigeant";
import { OutilsEpingles, HoteOutilParId } from "@/components/outils/HoteOutil";
import { AccueilResponsable } from "@/components/tableaux/AccueilResponsable";
import AccueilAgent, { EnteteAccueil } from "@/components/accueil/AccueilAgent";
import { useMessagerie } from "@/context/ThemeContext";
import { AccueilAgentWa } from "@/components/messagerie/AccueilWa";
import { AccueilDirigeantWa } from "@/components/messagerie/AccueilDirigeantWa";
import { Barre } from "@/components/messagerie/Wa";

const Stat = ({ label, valeur, testId, delai }) => (
  <div className="at-carte at-rise" style={{ animationDelay: `${delai}ms` }}>
    <p className="text-sm font-medium">{label}</p>
    <p className="text-[32px] leading-none font-medium tabular-nums mt-3" data-testid={testId}>{valeur}</p>
  </div>
);

export default function Accueil() {
  const { user } = useAuth();
  const messagerie = useMessagerie();
  const [params] = useSearchParams();
  if (messagerie && user.role === "agent") return <AccueilAgentWa />;
  if (messagerie && user.role === "dirigeant" && !params.get("vue")) return <AccueilDirigeantWa />;
  const contenu = user.role === "agent" ? <AccueilAgent /> : <AccueilClassique />;
  return messagerie ? <><Barre titre="Accueil" testId="wa-page-bar" /><div className="wa-defil">{contenu}</div></> : contenu;
}

function AccueilClassique() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const onglet = params.get("vue") === "tableau" ? "tableau" : "assistant";
  const [data, setData] = useState(null);
  const { vues, vue, setVue } = useMesVues("accueil");
  const [processus, setProcessus] = useState([]);
  const [pid, setPid] = useState(null);
  const { stats } = useStats(vues.length ? pid : null);
  useEffect(() => { api.get("/accueil").then((r) => setData(r.data)); }, []);
  useEffect(() => { if (vues.length) api.get("/processus").then((r) => { setProcessus(r.data); setPid(r.data[0]?.id); }); }, [vues.length]);

  return (
    <div data-testid="page-accueil">
      <EnteteAccueil user={user} sousTitre={`${new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} · ${user.role_label}${user.partenaire_nom ? ` · ${user.partenaire_nom}` : ""}`} />
      <div className="flex gap-2 my-4" role="tablist" data-testid="accueil-tabs">
        {[["assistant", "Mon assistant"], ["tableau", "Tableau de bord"]].map(([code, label]) => (
          <button key={code} type="button" role="tab" aria-selected={onglet === code} onClick={() => setParams(code === "assistant" ? {} : { vue: code })}
            className={`at-chip ${onglet === code ? "actif" : ""}`} data-testid={`accueil-tab-${code}`}>{label}</button>
        ))}
      </div>
      {onglet === "assistant" && <MonAssistant />}
      {onglet === "tableau" && <div className="mb-6"><OutilsEpingles lieu="accueil" titre="Mes outils épinglés" /></div>}
      {onglet === "tableau" && user.role === "dirigeant" && <AccueilDirigeant />}
      {onglet === "tableau" && user.role === "responsable" && <AccueilResponsable />}
      {onglet === "tableau" && ["dirigeant", "responsable", "agent"].includes(user.role) && <DevisSansRelance />}
      {onglet === "tableau" && user.role === "agent" && <PosteAgent />}
      {onglet === "tableau" && user.role === "confirmateur" && <PosteConfirmateur />}
      {onglet === "tableau" && user.role === "partenaire" && <PostePartenaire />}
      {onglet === "tableau" && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <Stat label="Fiches en cours" valeur={formatNombre(data.nb_fiches)} testId="stat-nb-fiches" delai={0} />
            <Stat label="Montant estimé" valeur={formatEuros(data.montant_total)} testId="stat-montant-total" delai={60} />
            {data.commission_totale !== undefined && <Stat label="Commissions" valeur={formatEuros(data.commission_totale)} testId="stat-commissions" delai={120} />}
            <Stat label="Sans action" valeur={data.sans_action} testId="stat-sans-action" delai={120} />
            <Stat label="Délai dépassé" valeur={data.en_retard} testId="stat-en-retard" delai={180} />
          </div>

          {vues.length > 0 && (
            <section className="mt-6 md:mt-8 min-w-0" data-testid="accueil-vues">
              <VuesOnglets vues={vues} vue={vue} setVue={setVue} testId="accueil-view" />
              {vue !== "couloirs" && <ProcessusPills processus={processus} pid={pid} setPid={setPid} testId="accueil-process" />}
              {vue === "carte_fleches" && <CarteFleches stats={stats} />}
              {vue === "entonnoir" && <Entonnoir stats={stats} />}
              {vue === "couloirs" && <Couloirs />}
              {vue?.startsWith("outil_") && pid && <HoteOutilParId id={vue.slice(6)} contexte={{ processus_id: pid }} hauteur={620} />}
              {vue === "pipeline_thermometre" && <p className="text-sm at-muted">Le pipeline thermomètre s'affiche dans l'onglet Pipeline.</p>}
            </section>
          )}

          {vues.length === 0 && data.processus.map((p) => {
            const total = p.etapes.reduce((s, e) => s + e.nombre, 0);
            return (
              <section key={p.id} className="at-card p-5 md:p-7 mt-4 md:mt-6" data-testid={`accueil-processus-${p.id}`}>
                <div className="flex items-baseline justify-between gap-3 mb-4">
                  <h2 className="text-base md:text-lg font-bold">{p.nom}</h2>
                  <span className="at-muted text-sm">{total} fiche{total > 1 ? "s" : ""}</span>
                </div>
                <div className="flex h-3 at-pill overflow-hidden gap-0.5" style={{ background: "var(--surface-2)" }}>
                  {p.etapes.filter((e) => e.nombre).map((e) => <span key={e.code} style={{ flex: e.nombre, background: e.temperature || "var(--muted)" }} title={`${e.nom} : ${e.nombre}`} />)}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mt-5">
                  {p.etapes.map((e) => (
                    <div key={e.code} className="at-soft p-3" data-testid={`etape-count-${p.id}-${e.code}`}>
                      <span className="block w-2.5 h-2.5 at-pill mb-2" style={{ background: e.temperature || "var(--muted)" }} />
                      <p className="text-xs at-muted font-semibold truncate">{e.nom}</p>
                      <p className="text-xl font-bold">{e.nombre}</p>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
          {data.processus.length === 0 && <p className="at-muted text-sm mt-4">Aucune fiche dans votre périmètre pour le moment.</p>}

          <section className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base md:text-lg font-bold">Fiches récentes</h2>
              <Link to="/fiches" className="text-sm font-semibold min-h-[44px] flex items-center" data-testid="accueil-voir-fiches">Tout voir</Link>
            </div>
            {data.recentes.length === 0 ? <Vide texte="Aucune fiche." testId="accueil-aucune-fiche" /> : (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">{data.recentes.map((f) => <FicheCarte key={f.id} fiche={f} />)}</div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
